import { EducationsForm } from "components/ResumeForm/EducationsForm";
import RichEditorWithSuggestions from "components/ResumeForm/Form/RichEditorWithSuggestions";
import { Input } from "components/ResumeForm/Form/InputGroup";
import { CreateHandleChangeArgsWithDescriptions } from "components/ResumeForm/types";
import { useAppSelector } from "lib/redux/hooks";
import { changeEducations, selectEducations } from "lib/redux/resumeSlice";
import { ResumeEducation } from "lib/redux/types";
import { calculateWordCount } from "lib/resume-scoring/content-details-sections/word-count";
import {
  IndividualEducationsAnalysis,
  ResumeAnalysis,
} from "lib/resume-scoring/resume-analysis/resume-analyse-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { NoIssueBox } from "../common/NoIssueBox";
import { getDateIssue } from "score-check/utils/getDateIssue";
import { checkDateStructureConsistent } from "score-check/utils/isDateStructureConsistent";
import { DateStructureConsistent } from "../common/DateStructureNotConsistent";
import { LongSentences } from "../common/LongSentences";
import { PassiveVoice } from "../common/PassiveVoice";
import { SectionHeader } from "../common/SectionHeader";
import { MistakesSection } from "../common/MistakesSection";
import { WordMistakesSection } from "../common/WordMistakesSection";
import { FixButtons } from "../common/FixButtons";
import { DateInputSection } from "../common/DateInputSection";
import { TextInputSection } from "../common/TextInputSection";
import { AddMoreSection } from "../common/AddMoreSection";
import { ReverseOrderWarning } from "../common/ReverseOrderWarning";
import { checkReverseOrder } from "score-check/utils/checkReverseOrder";
import { EducationScoreAnalysis } from "lib/resume-scoring/types";
import Image from "next/image";
import { MistakesDisplayV2 } from "../common/MistakesDisplayV2";
import { highlightSection, clearHighlight } from "lib/redux/highlightSlice";
import { GrammarMistakesSection } from "../common/GrammarMistakes";
import { DoesNotEndsWithPeriod } from "../common/DoesNotEndsWithPeriod";
import { PersonalPronouns } from "../common/PersonalPronouns";

export const EducationSection = ({
  score,
  maxScore,
  analysis,
}: {
  score: EducationScoreAnalysis;
  maxScore: number;
  analysis: ResumeAnalysis["educations"];
}) => {
  const [isAddEducationOpen, setIsAddEducationOpen] = useState(false);

  const educations = useAppSelector(selectEducations);

  // Check if educations are in reverse chronological order
  const educationInReverseOrder = !checkReverseOrder(
    analysis.individualEducationsAnalysis
  );

  return (
    <div className="scrollbar-hide relative mb-4 flex h-full max-h-[88.5vh] flex-col rounded-[12px] bg-white p-1 shadow-md lg:rounded-[6px]">
      {/* Fixed Sticky Header */}
      <SectionHeader
        title="Education"
        score={Math.floor(score.totalScore)}
        maxScore={maxScore}
      />

      {/* Scrollable Content - Hidden Scrollbar */}
      <div className="scrollbar-hide flex flex-1 flex-col gap-[25px] overflow-auto px-0 pb-3 lg:gap-[30px] lg:px-0 xl:gap-[40px]">
        {analysis.individualEducationsAnalysis.length === 0 && (
          <div className="flex flex-col items-center justify-between gap-3 rounded-xl bg-red-500/30 p-2">
            <div className="flex w-full items-center justify-between gap-3 p-3">
              <span className="text-sm text-black sm:text-base md:text-lg lg:text-xl">
                No education details found
              </span>
            </div>
          </div>
        )}
        {analysis.individualEducationsAnalysis.map((education, index) => (
          <IndividualEducation
            key={index}
            education={education}
            index={index}
            educations={educations}
          />
        ))}

        {educationInReverseOrder && (
          <ReverseOrderWarning sectionName="Education" />
        )}

        <AddMoreSection
          isOpen={isAddEducationOpen}
          toggleOpen={() => setIsAddEducationOpen(!isAddEducationOpen)}
          label="Education"
        />
        {isAddEducationOpen && (
          // <div>
          <EducationsForm />
          // <SaveButton isAddOpen={isAddEducationOpen} />
          // </div>
        )}
      </div>

      {/* Add custom scrollbar hide styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

const IndividualEducation = ({
  education,
  index,
  educations,
}: {
  education: IndividualEducationsAnalysis;
  index: number;
  educations: ResumeEducation[];
}) => {
  const isDateIssue = !education.startDate && !education.endDate;
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  const getHasDescriptionIssues = (): boolean => {
    let has = false;
    education.descriptionAnalysis.map((desc) => {
      if (hasActionVerbIssue) has = true;
      if (desc.analysis.buzzWords.length > 0) has = true;
      if (desc.analysis.fillerWords.length > 0) has = true;
      if (desc.analysis.grammarMistakes.length > 0) has = true;
      if (desc.analysis.spellingMistakes.length > 0) has = true;
      if (desc.analysis.isPassiveVoice == 1) has = true;
    });
    return has;
  };

  const dispatch = useDispatch();
  const handleEducationChange = (
    ...[field, value]: CreateHandleChangeArgsWithDescriptions<ResumeEducation>
  ) => {
    dispatch(changeEducations({ idx: index, field, value } as any));
  };

  // Get an array of non-past tense verbs that are the first word of a sentence
  // Deduplicate to show each unique verb only once
  const actionVerbIssues = [
    ...new Set(
      education.descriptionAnalysis.flatMap((desc) => {
        return desc.analysis.actionVerbAnalysis
          .filter((verb) => {
            // Check if the verb is not in past tense
            if (verb.isPastTense !== 0) return false;

            // Get the verb position in the statement
            const statement = desc.statement.trim();
            const firstWord = statement
              .split(" ")[0]
              .toLowerCase()
              .replace(/[^\w]/g, "");

            // Only include if it's the first word of the sentence
            return firstWord === verb.verb.toLowerCase();
          })
          .map((verb) => verb.verb);
      })
    ),
  ];

  const hasActionVerbIssue = actionVerbIssues.length > 0;
  const hasBuzzWordsIssue = education.descriptionAnalysis.some(
    (desc) => desc.analysis.buzzWords.length > 0
  );
  const hasFillerWordsIssue = education.descriptionAnalysis.some(
    (desc) => desc.analysis.fillerWords.length > 0
  );
  const hasPassiveVoiceIssue = education.descriptionAnalysis.some(
    (desc) => desc.analysis.isPassiveVoice == 1
  );
  const hasGrammarMistakes = education.descriptionAnalysis.some(
    (desc) =>
      desc.analysis.grammarMistakes && desc.analysis.grammarMistakes.length > 0
  );
  const hasSpellingMistakes = education.descriptionAnalysis.some(
    (desc) =>
      desc.analysis.spellingMistakes &&
      desc.analysis.spellingMistakes.length > 0
  );

  // Deduplicate sentences (unlikely but possible to have exact duplicates)
  const longSentences = [
    ...new Set(
      education.descriptionAnalysis
        .filter((desc) => calculateWordCount(desc.statement) > 25)
        .map((desc) => desc.statement)
    ),
  ];

  const dateIssueText = getDateIssue(education.startDate, education.endDate);
  // const isDateStructureConsistent = checkDateStructureConsistent(education.startDateEntry, education.endDateEntry);
  const isDateStructureConsistent = checkDateStructureConsistent(
    education.startDateEntry,
    education.endDateEntry
  );
  const hasDescriptionIssues = getHasDescriptionIssues();

  // Section-level highlighting handlers
  const handleSectionMouseEnter = () => {
    dispatch(
      highlightSection({
        section: "education",
        itemIndex: index,
      })
    );
  };

  const handleSectionMouseLeave = () => {
    dispatch(clearHighlight());
  };

  // Local repeated action verbs for this experience (derived from descriptionAnalysis)
  const localRepeatedActionVerbs = (() => {
    const verbs: string[] = education.descriptionAnalysis.flatMap((d) =>
      d.analysis.actionVerbAnalysis.map((v) => v.verb)
    );
    const counts: Record<string, number> = {};
    verbs.forEach((v) => {
      const key = (v || "").trim().toLowerCase();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts)
      .filter((k) => counts[k] > 1)
      .map((k) => k);
  })();

  // Local repeated phrases (simple trigram-based detection across this experience statements)
  const localRepeatedPhrases = (() => {
    const shingles: string[] = [];
    const normalize = (s: string) =>
      s
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ");
    education.descriptionAnalysis.forEach((d) => {
      const text = normalize(d.statement || "");
      if (!text) return;
      const words = text.split(" ").filter(Boolean);
      const n = 3; // trigrams
      for (let i = 0; i + n <= words.length; i++) {
        shingles.push(words.slice(i, i + n).join(" "));
      }
    });
    const counts: Record<string, number> = {};
    shingles.forEach((s) => (counts[s] = (counts[s] || 0) + 1));
    return Object.keys(counts).filter((k) => counts[k] > 1);
  })();

  // Filter out null/falsy values and deduplicate
  const spellingMistakes = [
    ...new Set(
      education.descriptionAnalysis
        .flatMap((point) =>
          point.analysis.spellingMistakes.map((mistake) => mistake.incorrect)
        )
        .filter(Boolean) as string[]
    ),
  ];

  const buzzWords = [
    ...new Set(
      education.descriptionAnalysis
        .flatMap((point) => point.analysis.buzzWords)
        .filter(Boolean) as string[]
    ),
  ];

  // Split into words and phrases
  const repeatedWords = localRepeatedActionVerbs; // Already deduplicated in the logic above
  const repeatedPhrases = localRepeatedPhrases; // Already deduplicated in the logic above

  // Grammar mistakes - filter out null/falsy values and deduplicate
  const grammarMistakes = [
    ...new Set(
      education.descriptionAnalysis
        .flatMap((point) =>
          point.analysis.grammarMistakes.map((m) => m.incorrect)
        )
        .filter(Boolean) as string[]
    ),
  ];
  const personalPronouns = [
    ...new Set(
      education.descriptionAnalysis
        .filter((desc) => desc.analysis.personalPronouns.length > 0)
        .map((desc) => desc.analysis.personalPronouns)
        .flat()
    ),
  ];
  const doesNotEndsWithPeriod = [
     ...new Set(education.descriptionAnalysis
          .filter((desc) => desc.analysis.endsWithPeriod === 0)
          .map((desc) => desc.statement))
  ];

  const isMissingInfo = isDateIssue || !education.school || !education.gpa;
  const hasIssues =
    hasDescriptionIssues ||
    isDateIssue ||
    !education.school ||
    !education.gpa ||
    !isDateStructureConsistent ||
    repeatedPhrases.length > 0 ||
    repeatedWords.length > 0 ||
    grammarMistakes.length > 0 ||
    spellingMistakes.length > 0 ||
    longSentences.length > 0 ||
    hasPassiveVoiceIssue ||
    hasActionVerbIssue ||
    !isDateStructureConsistent ||
    buzzWords.length > 0 ||
    personalPronouns.length > 0 ||
    repeatedWords.length > 0;
  return (
    <div className="mt-0 flex flex-col gap-4 rounded-2xl bg-gradient-to-br from-white to-gray-50 p-4 pt-0 font-montserrat shadow-sm transition-shadow hover:shadow-md lg:gap-6 lg:p-2 lg:pt-0 xl:mt-2 xl:gap-8 xl:p-4 xl:pt-0">
      <span
        className="sticky top-0 z-10 mx-auto flex cursor-pointer items-center justify-center gap-3 rounded-xl bg-gray-100 px-[16px] py-[4px] text-center text-sm font-semibold text-black shadow-md transition-opacity hover:opacity-90 md:text-lg lg:px-[20px] lg:py-[8px] lg:text-xl xl:px-[24px] xl:py-[8px]"
        onMouseEnter={handleSectionMouseEnter}
        onMouseLeave={handleSectionMouseLeave}
      >
        Education {index + 1}
      </span>

      {/* Missing Information*/}
      {isMissingInfo && (
        <div className="relative mt-3 flex flex-col gap-[10px] rounded-xl bg-[#FEE7E4] px-[15px] py-[10px] lg:gap-[16px] lg:px-[20px] lg:py-[14px] xl:mt-1">
          <div className="absolute left-4 top-[-16px] flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
            <Image
              src="/icons/score-check/missing.svg"
              height={24}
              width={24}
              alt="icon"
              className="h-4 w-4 lg:h-5 lg:w-5"
            />
            <span className="text-sm font-bold lg:text-base xl:text-lg">
              Missing Information
            </span>
          </div>

          <div className="mt-6">
            <div className="flex flex-col gap-2">
              {!education.school && (
                <TextInputSection
                  value={educations[index].school}
                  placeholder="IIT Delhi"
                  fieldName="school"
                  missingText="School name missing"
                  handleChange={(field, value) =>
                    handleEducationChange(field, value)
                  }
                  icon="duration"
                />
              )}

              {isDateIssue && (
                <DateInputSection
                  startDate={educations[index].startDate}
                  endDate={educations[index].endDate}
                  dateIssueText={dateIssueText}
                  handleChange={(field, value) =>
                    handleEducationChange(field, value)
                  }
                />
              )}

              {!education.gpa && (
                <TextInputSection
                  value={educations[index].gpa}
                  placeholder="7.34"
                  fieldName="gpa"
                  missingText="GPA missing"
                  handleChange={(field, value) =>
                    handleEducationChange(field, value)
                  }
                  icon="duration"
                />
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-col items-center justify-center gap-3">
            <div className="relative h-32 w-32 lg:h-40 lg:w-40">
              <Image
                src="/assets/resumeScore/missingInfo.png"
                alt="Long sentences illustration"
                fill
                className="object-contain"
              />
            </div>
            <p className="text-center text-sm font-semibold text-red-600 lg:text-base">
              Missing information can weaken your resume
            </p>
          </div>
        </div>
      )}

      {/* Mistakes Section */}
      {/* {hasDescriptionIssues && (
        <MistakesSection
          mistakes={education.descriptionAnalysis
            .filter((point) => {
              // Only include statements that have at least one issue
              return (
                point.analysis.grammarMistakes.length > 0 ||
                point.analysis.spellingMistakes.length > 0 ||
                point.analysis.buzzWords.length > 0 ||
                point.analysis.fillerWords.length > 0 ||
                point.analysis.isPassiveVoice === 1 ||
                point.analysis.personalPronouns.length > 0 ||
                (point.analysis.actionVerbAnalysis.length > 0 &&
                  point.analysis.actionVerbAnalysis.some(
                    (verb) => verb.isPastTense === 0
                  )) ||
                point.analysis.endsWithPeriod === 0 ||
                calculateWordCount(point.statement) > 25
              );
            })
            .map((point) => point.statement)}
        />
      )} */}

      <MistakesDisplayV2
        spellingMistakes={spellingMistakes}
        buzzWords={buzzWords}
        repeatedWords={repeatedWords}
        section="education"
        itemIndex={index}
      />

      {hasGrammarMistakes && grammarMistakes.length > 0 && (
        <GrammarMistakesSection
          title="Grammar mistake"
          icon="grammarMistake"
          section="education"
          itemIndex={index}
          mistakes={grammarMistakes}
        />
      )}

      {/* Repeated Phrases */}
      {repeatedPhrases.length > 0 && (
        <WordMistakesSection
          title="Repeated phrases"
          icon="Announcement"
          section="education"
          itemIndex={index}
          mistakeType="filler"
          mistakes={repeatedPhrases}
        />
      )}

      {/* {hasSpellingMistakes && (
        <WordMistakesSection
          title="Spelling mistake"
          icon="spellingMistake"
          mistakes={education.descriptionAnalysis.flatMap((point) =>
            point.analysis.spellingMistakes.map((mistake) => mistake.incorrect)
          )}
        />
      )}

      {hasBuzzWordsIssue && (
        <WordMistakesSection
          title="Buzz words found"
          icon="spellingMistake"
          mistakes={education.descriptionAnalysis.flatMap(
            (point) => point.analysis.buzzWords
          )}
        />
      )} */}

      {hasActionVerbIssue && (
        <WordMistakesSection
          title="Past tense not used"
          icon="spellingMistake"
          section="education"
          itemIndex={index}
          mistakeType="actionVerb"
          mistakes={actionVerbIssues}
        />
      )}

      {personalPronouns.length > 0 && (
        <PersonalPronouns
          personalPronouns={personalPronouns}
          section="education"
          itemIndex={index}
        />
      )}

      
     { doesNotEndsWithPeriod.length > 0 && (
      <DoesNotEndsWithPeriod
        doesNotEndsWithPeriod={doesNotEndsWithPeriod}
        section="education"
        itemIndex={index}
      />)}
      

      {hasPassiveVoiceIssue && <PassiveVoice />}

      <LongSentences
        longSentences={longSentences}
        section="education"
        itemIndex={index}
      />

      {hasIssues && (
        <>
          {isDescriptionOpen && (
            <div className="mx-auto flex w-full flex-col items-center justify-center gap-3 rounded-xl bg-white p-4 lg:p-6">
              {/* School Name */}
              <Input
                label="School"
                labelClassName="w-full"
                name="school"
                placeholder="IIT Delhi"
                value={educations[index].school}
                onChange={handleEducationChange}
              />

              {/* Start Date and End Date */}
              <div className="grid w-full grid-cols-2 gap-3">
                <Input
                  label="Start Date"
                  labelClassName="w-full"
                  name="startDate"
                  placeholder="June 2024"
                  value={educations[index].startDate}
                  onChange={handleEducationChange}
                />
                <Input
                  label="End Date"
                  labelClassName="w-full"
                  name="endDate"
                  placeholder="June 2024"
                  value={educations[index].endDate}
                  onChange={handleEducationChange}
                />
              </div>

              {/* Degree and GPA */}
              <div className="grid w-full grid-cols-3 gap-3">
                <Input
                  label="Degree & Major"
                  labelClassName="col-span-2"
                  name="degree"
                  placeholder="Bachelor of Technology (IT)"
                  value={educations[index].degree}
                  onChange={handleEducationChange}
                />
                <Input
                  label="GPA / Percentage"
                  labelClassName="w-full"
                  name="gpa"
                  placeholder="7.43"
                  value={educations[index].gpa}
                  onChange={handleEducationChange}
                />
              </div>

              {/* Description Editor */}
              <div className="w-full">
                <RichEditorWithSuggestions
                  name="descriptions"
                  value={educations[index].descriptions?.map((desc) => desc)}
                  onChange={handleEducationChange}
                  placeholder="Free paragraph space to list out additional activities, courses, awards etc"
                  section="educations"
                />
              </div>
            </div>
          )}
          <FixButtons
            isEditMode={isDescriptionOpen}
            toggleEditMode={() => setIsDescriptionOpen(!isDescriptionOpen)}
          />
        </>
      )}
      <DateStructureConsistent
        isDateStructureConsistent={isDateStructureConsistent}
      />

      {!hasDescriptionIssues && (
        <>
          <NoIssueBox message="No issues found in this education description. Great job!" />
        </>
      )}
    </div>
  );
};
